/**
 * DocsApp_v2 Configuration
 * Defines all supported classes and app settings
 */

import { AppConfig, SupportedClass } from '../../../services/baseApplication/types';

/**
 * Supported classes for DocsApp_v2
 * Each class has a description, icon, and optional default size
 */
export const DOCS_SUPPORTED_CLASSES: Record<string, SupportedClass> = {
  // Text Elements
  'paragraph': {
    description: 'Standard paragraph text',
    icon: '¶',
    render: (node) => {
      const el = document.createElement('p');
      el.className = 'paragraph';
      el.textContent = node.text || '';
      return el;
    }
  },
  
  // Headings
  'heading-1': {
    description: 'Top-level heading (H1)',
    icon: 'H1',
    render: (node) => {
      const el = document.createElement('h1');
      el.className = 'heading-1';
      el.textContent = node.text || '';
      return el;
    }
  },
  'heading-2': {
    description: 'Second-level heading (H2)',
    icon: 'H2',
    render: (node) => {
      const el = document.createElement('h2');
      el.className = 'heading-2';
      el.textContent = node.text || '';
      return el;
    }
  },
  'heading-3': {
    description: 'Third-level heading (H3)',
    icon: 'H3',
    render: (node) => {
      const el = document.createElement('h3');
      el.className = 'heading-3';
      el.textContent = node.text || '';
      return el;
    }
  },
  'heading-4': {
    description: 'Fourth-level heading (H4)',
    icon: 'H4',
    render: (node) => {
      const el = document.createElement('h4');
      el.className = 'heading-4';
      el.textContent = node.text || '';
      return el;
    }
  },
  'heading-5': {
    description: 'Fifth-level heading (H5)',
    icon: 'H5',
    render: (node) => {
      const el = document.createElement('h5');
      el.className = 'heading-5';
      el.textContent = node.text || '';
      return el;
    }
  },
  'heading-6': {
    description: 'Sixth-level heading (H6)',
    icon: 'H6',
    render: (node) => {
      const el = document.createElement('h6');
      el.className = 'heading-6';
      el.textContent = node.text || '';
      return el;
    }
  },
  
  // Lists
  'bullet-list': {
    description: 'Unordered bullet list',
    icon: '•',
    render: (node) => {
      const el = document.createElement('ul');
      el.className = 'bullet-list';
      return el;
    }
  },
  'ordered-list': {
    description: 'Numbered list',
    icon: '1.',
    render: (node) => {
      const el = document.createElement('ol');
      el.className = 'ordered-list';
      return el;
    }
  },
  'list-item': {
    description: 'Individual list item',
    icon: '◦',
    render: (node) => {
      const el = document.createElement('li');
      el.className = 'list-item';
      el.textContent = node.text || '';
      return el;
    }
  },
  
  // Code
  'code-block': {
    description: 'Code block with syntax highlighting',
    icon: '</>', 
    render: (node) => {
      const el = document.createElement('pre');
      el.className = 'code-block';
      const code = document.createElement('code');
      code.textContent = node.text || '';
      el.appendChild(code);
      return el;
    }
  },
  'inline-code': {
    description: 'Inline code',
    icon: '`',
    render: (node) => {
      const el = document.createElement('code');
      el.className = 'inline-code';
      el.textContent = node.text || '';
      return el;
    }
  },
  
  // Quotes
  'blockquote': {
    description: 'Block quote',
    icon: '"',
    render: (node) => {
      const el = document.createElement('blockquote');
      el.className = 'blockquote';
      el.textContent = node.text || '';
      return el;
    }
  },
  
  // Callouts
  'callout-info': {
    description: 'Info callout (blue)',
    icon: 'ℹ',
    render: (node) => {
      const el = document.createElement('div');
      el.className = 'callout callout-info';
      el.textContent = node.text || '';
      return el;
    }
  },
  'callout-warning': {
    description: 'Warning callout (yellow)',
    icon: '⚠',
    render: (node) => {
      const el = document.createElement('div');
      el.className = 'callout callout-warning';
      el.textContent = node.text || '';
      return el;
    }
  },
  'callout-success': {
    description: 'Success callout (green)',
    icon: '✓',
    render: (node) => {
      const el = document.createElement('div');
      el.className = 'callout callout-success';
      el.textContent = node.text || '';
      return el;
    }
  },
  'callout-error': {
    description: 'Error callout (red)',
    icon: '✗',
    render: (node) => {
      const el = document.createElement('div');
      el.className = 'callout callout-error';
      el.textContent = node.text || '';
      return el;
    }
  },
  
  // Separators
  'horizontal-rule': {
    description: 'Horizontal divider line',
    icon: '—',
    render: (node) => {
      const el = document.createElement('hr');
      el.className = 'horizontal-rule';
      return el;
    }
  },
  
  // Media
  'image': {
    description: 'Image',
    icon: '🖼',
    render: (node) => {
      const el = document.createElement('img');
      el.className = 'image';
      el.src = node.attrs?.src || '';
      el.alt = node.attrs?.alt || '';
      return el;
    },
    defaultSize: { width: 500, height: 300 }
  },
  'video': {
    description: 'Video',
    icon: '🎥',
    render: (node) => {
      const el = document.createElement('video');
      el.className = 'video';
      el.src = node.attrs?.src || '';
      el.controls = true;
      return el;
    },
    defaultSize: { width: 640, height: 360 }
  },
  'embed': {
    description: 'Embedded content (iframe)',
    icon: '📦',
    render: (node) => {
      const el = document.createElement('iframe');
      el.className = 'embed';
      el.src = node.attrs?.src || '';
      return el;
    },
    defaultSize: { width: 640, height: 400 }
  },
  
  // Interactive
  'table': {
    description: 'Table',
    icon: '▦',
    render: (node) => {
      const el = document.createElement('table');
      el.className = 'table';
      return el;
    }
  },
  'table-row': {
    description: 'Table row',
    icon: '━',
    render: (node) => {
      const el = document.createElement('tr');
      el.className = 'table-row';
      return el;
    }
  },
  'table-cell': {
    description: 'Table cell',
    icon: '□',
    render: (node) => {
      const el = document.createElement('td');
      el.className = 'table-cell';
      el.textContent = node.text || '';
      return el;
    }
  },
  'table-header': {
    description: 'Table header cell',
    icon: '■',
    render: (node) => {
      const el = document.createElement('th');
      el.className = 'table-header';
      el.textContent = node.text || '';
      return el;
    }
  },
  'checkbox': {
    description: 'Checkbox (for to-do lists)',
    icon: '☐',
    render: (node) => {
      const el = document.createElement('input');
      el.type = 'checkbox';
      el.className = 'checkbox';
      el.checked = node.attrs?.checked || false;
      return el;
    }
  },
  'link': {
    description: 'Hyperlink',
    icon: '🔗',
    render: (node) => {
      const el = document.createElement('a');
      el.className = 'link';
      el.href = node.attrs?.href || '';
      el.textContent = node.text || '';
      el.target = node.attrs?.target || '_blank';
      return el;
    }
  }
};

/**
 * DocsApp_v2 Configuration
 */
export const DOCS_APP_CONFIG: AppConfig = {
  // Identity
  appId: 'docs_v2',
  appName: 'Documents v2',
  
  // Container setup
  containerType: 'page',
  layoutMode: 'flow',
  
  // Supported classes (25+ classes)
  supportedClasses: DOCS_SUPPORTED_CLASSES,
  
  // Positioning rules (flow layout only)
  supportedPositionTypes: ['static', 'relative'],
  
  // Interactions (no drag/drop for docs)
  supportsDragDrop: false,
  supportsResize: false,
  supportsRotation: false,
  
  // Container defaults
  defaultContainer: {
    width: 850, // Standard document width
    height: null, // Infinite scroll
    padding: 40
  },
  
  // UI
  editorSelector: '.docs-editor-v2'
};

/**
 * Get list of all supported class names
 */
export function getSupportedClassNames(): string[] {
  return Object.keys(DOCS_SUPPORTED_CLASSES);
}

/**
 * Check if a className is supported
 */
export function isClassNameSupported(className: string): boolean {
  return className in DOCS_SUPPORTED_CLASSES;
}

/**
 * Get class info by name
 */
export function getClassInfo(className: string): SupportedClass | undefined {
  return DOCS_SUPPORTED_CLASSES[className];
}

